import { create } from "zustand";
import axios from "axios";
import { toast } from "sonner";
import { DEFAULT_NORMAL_ITEMS } from "../lib/deliveryUtils";
import BACKEND_URL from "../lib/apiConfig";
import { createDeliveryListFetch } from "../lib/deliveryListFetch";
import { beginMutation, endMutation, cancelMutation } from "../lib/mutationGuard";

const API = `${BACKEND_URL}/api`;

const useDeliveriesStore = create((set, get) => ({
  // ===== Data =====
  deliveries: [],
  loading: true,
  syncing: false,
  lastSyncedAt: null,
  normalItems: DEFAULT_NORMAL_ITEMS,
  normalItemsLoading: true,

  // ===== Data fetching =====
  fetchNormalItems: async () => {
    try {
      const res = await axios.get(`${API}/normal-items`);
      set({ normalItems: res.data.items || DEFAULT_NORMAL_ITEMS, normalItemsLoading: false });
    } catch {
      const saved = localStorage.getItem("deliveries_normal_items");
      if (saved) set({ normalItems: JSON.parse(saved) });
      set({ normalItemsLoading: false });
    }
  },

  fetchDeliveries: createDeliveryListFetch(API, set, get, () => toast.error("Failed to load deliveries")),

  // ===== Sync =====
  handleSync: async () => {
    set({ syncing: true });
    try {
      const res = await axios.post(`${API}/deliveries/sync?full=true`);
      if (res.data.skipped) {
        toast.info("Delivery check skipped; refreshing saved deliveries");
      } else {
        const message = res.data.total_synced != null
          ? `Synced ${res.data.total_synced} deliveries from Airtable`
          : `Checked ${res.data.checked ?? 0} deliveries`;
        toast.success(message);
        set({ lastSyncedAt: res.data.synced_at || new Date().toISOString() });
      }
      await get().fetchDeliveries();
    } catch {
      toast.error("Failed to sync deliveries");
    } finally {
      set({ syncing: false });
    }
  },

  // ===== Actions =====
  handleAddressUpdate: async (deliveryId, newAddress) => {
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, delivery_address: newAddress, _syncing: true } : d)) });
    try {
      const res = await axios.patch(`${API}/deliveries/${deliveryId}`, { delivery_address: newAddress });
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, delivery_address: newAddress, _syncing: false } : d)) });
      toast.success(res.data.airtable_push_result === "success" ? "Address saved & synced to Airtable" : "Address saved locally");
    } catch {
      toast.error("Failed to update address");
      get().fetchDeliveries();
    }
  },

  handleCustomItemStatusUpdate: async (deliveryId, itemName, newStatus) => {
    const currentStatuses = get().deliveries.find((d) => d.id === deliveryId)?.custom_item_statuses || {};
    set({
      deliveries: get().deliveries.map((d) =>
        d.id === deliveryId ? { ...d, custom_item_statuses: { ...d.custom_item_statuses, [itemName]: newStatus }, _syncing: true } : d
      ),
    });
    try {
      const res = await axios.patch(`${API}/deliveries/${deliveryId}`, { custom_item_statuses: { ...currentStatuses, [itemName]: newStatus } });
      set({
        deliveries: get().deliveries.map((d) =>
          d.id === deliveryId ? { ...d, custom_item_statuses: { ...d.custom_item_statuses, [itemName]: newStatus }, _syncing: false } : d
        ),
      });
      toast.success(res.data.airtable_push_result === "success" ? "Custom item status saved & synced to Airtable" : "Custom item status saved locally");
    } catch {
      toast.error("Failed to update custom item status");
      get().fetchDeliveries();
    }
  },

  handleInternalNotesUpdate: async (deliveryId, newNotes) => {
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, internal_notes: newNotes, _syncing: true } : d)) });
    try {
      const res = await axios.patch(`${API}/deliveries/${deliveryId}`, { internal_notes: newNotes });
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, internal_notes: newNotes, _syncing: false } : d)) });
      toast.success(res.data.airtable_push_result === "success" ? "Notes saved & synced to Airtable" : "Notes saved locally");
    } catch {
      toast.error("Failed to update notes");
      get().fetchDeliveries();
    }
  },

  handleShopNotesUpdate: async (deliveryId, newNotes) => {
    // iter 370i8 — Stamp the author so the Shop Tech queue card can
    // show "— <name>" under the preview. Read from localStorage so
    // we don't need to thread the user object through every call site.
    const updatedBy = (typeof localStorage !== "undefined" && localStorage.getItem("user_name")) || "";
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, shop_notes: newNotes, shop_notes_updated_by: updatedBy, _syncing: true } : d)) });
    try {
      const res = await axios.patch(`${API}/deliveries/${deliveryId}`, { shop_notes: newNotes, shop_notes_updated_by: updatedBy });
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, shop_notes: newNotes, shop_notes_updated_by: updatedBy, _syncing: false } : d)) });
      toast.success(res.data.airtable_push_result === "success" ? "Shop notes saved & synced" : "Shop notes saved locally");
    } catch {
      toast.error("Failed to update shop notes");
      get().fetchDeliveries();
    }
  },

  handleResetCart: async (deliveryId) => {
    try {
      await axios.post(`${API}/shop/cart/${deliveryId}/reset`);
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, status: "In Queue", shop_tech_name: null } : d)) });
      toast.success("Cart reset to queue");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset cart");
      get().fetchDeliveries();
    }
  },

  handleStatusUpdate: async (deliveryId, newStatus) => {
    beginMutation(deliveryId);
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, status: newStatus, _syncing: true } : d)) });
    try {
      const res = await axios.patch(`${API}/deliveries/${deliveryId}`, { status: newStatus });
      endMutation(deliveryId);
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, status: newStatus, _syncing: false } : d)) });
      toast.success(res.data.airtable_push_result === "success" ? "Status updated & synced to Airtable" : "Status updated locally");
    } catch {
      cancelMutation(deliveryId);
      toast.error("Failed to update status");
      get().fetchDeliveries();
    }
  },

  // Toggle the "confirmed delivery date" date-lock. Uses proper Zustand
  // setState (so React re-renders) instead of direct array mutation, and
  // does NOT refetch on success (the local Mongo write is authoritative —
  // refetching here used to fight the 45-second Airtable sync poll and
  // cause the lock to flicker / revert before Airtable caught up).
  toggleDateLock: async (deliveryId) => {
    const current = get().deliveries.find((d) => d.id === deliveryId);
    if (!current) return;
    const newVal = !current.confirmed_delivery_date;
    // Optimistic flip with _syncing flag for visual feedback
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, confirmed_delivery_date: newVal, _syncing: true } : d)) });
    try {
      await axios.patch(`${API}/deliveries/${deliveryId}`, { confirmed_delivery_date: newVal });
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, confirmed_delivery_date: newVal, _syncing: false } : d)) });
    } catch (err) {
      // Real revert on failure (server rejected the change)
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, confirmed_delivery_date: !newVal, _syncing: false } : d)) });
      toast.error(err.response?.data?.detail || "Failed to update date lock");
    }
  },

  toggleDetailed: async (deliveryId) => {
    const current = get().deliveries.find((d) => d.id === deliveryId);
    if (!current) return;
    const newVal = !current.detailed;
    set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, detailed: newVal, _syncing: true } : d)) });
    try {
      await axios.patch(`${API}/deliveries/${deliveryId}`, { detailed: newVal });
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, detailed: newVal, _syncing: false } : d)) });
    } catch (err) {
      set({ deliveries: get().deliveries.map((d) => (d.id === deliveryId ? { ...d, detailed: !newVal, _syncing: false } : d)) });
      toast.error(err.response?.data?.detail || "Failed to update detail flag");
    }
  },

  markAsNormalItem: async (item) => {
    const keyword = item.toLowerCase().trim();
    try {
      await axios.post(`${API}/normal-items`, { keyword });
      set({ normalItems: [...get().normalItems, keyword] });
      toast.success(`"${item}" added to normal items`);
    } catch {
      if (!get().normalItems.includes(keyword)) {
        set({ normalItems: [...get().normalItems, keyword] });
      }
    }
  },

  setNormalItems: (v) => set({ normalItems: typeof v === "function" ? v(get().normalItems) : v }),
}));

export default useDeliveriesStore;
